/**
 * YourTurn — the "Your Turn" section of the combat right rail.
 *
 * Hosts the focused slot + passive slots, combo upgrade row, clash contribution
 * subsection, and the Submit declarations button.
 *
 * Slot composition rules (spec §6, plan Task 7.1):
 * - One focused-slot ActionDeclarationCard.
 * - Passive cards ONLY for categories NOT used by focused. When focused=Physical,
 *   render Social + Mental passives. No disabled-placeholder for the focused category.
 *
 * Phase 7 of the unified-combat-ui plan.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { ActionDeclarationCard } from '@/actions/ActionDeclarationCard';
import type {
  ActionContext,
  ActionSlot,
  CastPosition,
  EffortLevel,
  PositionTargetShape,
  TargetOption,
} from '@/actions/types';
import type { PlayerAction, SoulfrayWarningData } from '@/scenes/actionTypes';
import { MovementActions } from '../components/MovementActions';
import { SoulfrayAcceptGate } from '../components/SoulfrayAcceptGate';
import { FuryDeclaration } from '../components/FuryDeclaration';
import { ThreadPullDialog, type PullSelection } from '@/magic/components/threads/ThreadPullDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  combatKeys,
  invalidateConsequenceOutcomes,
  useAvailableCombos,
  useCoverMutation,
  useDispatchPlayerAction,
  useFleeMutation,
  useGuardMutation,
  useUpgradeCombo,
} from '../queries';
import { isDispatchFailure } from '../types';
import type {
  AvailableCombo,
  DispatchActionRequest,
  DispatchResult,
  EncounterDetail,
  Participant,
  PositionNode,
  RoundActionTyped,
} from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface YourTurnProps {
  encounterId: number;
  characterId: number;
  characterSheetId: number;
  /** Current encounter round number — resets the submitted state when it changes. */
  roundNumber: number;
  /** Available PlayerActions for the character — caller filters COMBAT backend. */
  availableActions: PlayerAction[];
  readOnly?: boolean;
  /** Strain slider max — typically ParticipantSerializer.available_strain.
   *  Falls back to 10 if not provided. */
  availableStrain?: number | null;
  /**
   * Full encounter detail — used to gate flee/cover controls on declaring phase
   * and to resolve ally names for the cover picker. Optional so callers that
   * don't have encounter data yet can still render the slot composition.
   */
  encounter?: EncounterDetail | null;
  /**
   * Cast-time position selection, controlled by the caller (#2206). CombatTurnPanel/
   * CombatRail lift this above the rail tabs so the tactical-map tab can share
   * it with this panel. Optional and falls back to local `useState` when the caller
   * doesn't provide it (e.g. tests rendering YourTurn standalone) — unlike
   * ActionDeclarationCard's castPosition prop, which has no such local-state
   * fallback and simply no-ops without a caller-supplied setter.
   */
  castPosition?: CastPosition;
  onCastPositionChange?: (next: CastPosition) => void;
  /**
   * Reports the currently-selected focused technique's position-targeting shape
   * to the caller (#2206) — lets a sibling tab (the tactical map) know whether
   * map-click position-picking should be active, even after this panel unmounts
   * (rail tabs unmount their inactive TabsContent).
   */
  onPositionShapeChange?: (shape: PositionTargetShape) => void;
}

// ---------------------------------------------------------------------------
// Passive slot categories
// ---------------------------------------------------------------------------

const PASSIVE_SLOTS: ActionSlot[] = ['passive-physical', 'passive-social', 'passive-mental'];

/**
 * Map the UI's uppercase EffortLevel to the backend's lowercase
 * fatigue.EffortLevel TextChoices value (the value stored on CombatRoundAction
 * and keyed in EFFORT_CHECK_MODIFIER). The COMBAT dispatch requires
 * `effort_level` in kwargs on every declaration (focused + each passive) or it
 * rejects with UNKNOWN_ACTION_REF. The UI's 'VERY_HIGH' tier maps to the
 * backend's 'extreme'.
 */
const EFFORT_TO_BACKEND: Record<EffortLevel, string> = {
  VERY_LOW: 'very_low',
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  VERY_HIGH: 'extreme',
};

/**
 * Display labels for `PlayerAction.protective_flavor` (#2207) — mirrors
 * PROTECTIVE_FLAVOR_BARRIER/_BLINK/_REDIRECT in
 * world/magic/services/targeting.py. Purely cosmetic (Guard technique picker).
 */
const PROTECTIVE_FLAVOR_LABELS: Record<string, string> = {
  barrier: 'Barrier',
  blink: 'Blink',
  redirect: 'Redirect',
};

/** Sentinel Select values — Radix Select disallows an empty-string item value. */
const GUARD_ANYONE_VALUE = '__anyone__';
const GUARD_NO_TECHNIQUE_VALUE = '__none__';
/** Redirect destination sentinel (#2210) — the universal fallback; also the
 *  default when the picker's kwargs are omitted entirely. */
const GUARD_DESTINATION_AWAY = '__away__';
/** Redirect destination Select value prefixes (#2210) — parsed in handleGuard. */
const GUARD_DESTINATION_OPPONENT_PREFIX = 'opponent:';
const GUARD_DESTINATION_OBJECT_PREFIX = 'object:';

/**
 * Derive the focused slot's category from the selected technique's
 * `action_category` (#614), surfaced on the PlayerAction descriptor. The
 * matching passive slot is hidden (spec §6). Returns null when no focused
 * technique is selected or its category is unset.
 */
function resolveFocusedCategory(
  context: ActionContext,
  availableActions: PlayerAction[]
): ActionSlot | null {
  if (context.techniqueId === undefined) return null;
  const selected = availableActions.find((a) => a.ref.technique_id === context.techniqueId);
  if (!selected?.action_category) return null;
  return `passive-${selected.action_category}` as ActionSlot;
}

/**
 * Return the passive slot names that should be rendered given the focused
 * category. The focused category's passive slot is hidden entirely (spec §6).
 */
function passiveSlotsToRender(focusedCategory: ActionSlot | null): ActionSlot[] {
  if (focusedCategory === null) return PASSIVE_SLOTS;
  return PASSIVE_SLOTS.filter((s) => s !== focusedCategory);
}

// ---------------------------------------------------------------------------
// Initial context factory
// ---------------------------------------------------------------------------

function initialContext(slot: ActionSlot): ActionContext {
  return {
    slot,
    effort: 'MEDIUM',
    strainCommitment: 0,
  };
}

// ---------------------------------------------------------------------------
// Round-scoped state (#2423 finding 4) — consolidated into one object so a
// round advance resets it WHOLESALE, making "forgot to reset one atom" bugs
// structurally impossible (previously selectedClashRef/strainByClash/
// focusedContext/passiveContexts/submitError survived a round advance and
// leaked a stale clash selection into the next round's submit).
// ---------------------------------------------------------------------------

interface RoundScopedState {
  focusedContext: ActionContext;
  passiveContexts: Partial<Record<ActionSlot, ActionContext>>;
  selectedClashRef: PlayerAction['ref'] | null;
  strainByClash: Record<number, number>;
  submitted: boolean;
  submitError: string | null;
  pullDialogOpen: boolean;
  selectedPull: PullSelection | null;
  soulfrayAccepted: boolean;
  furyTierId: number | null;
  furyAnchorId: number | null;
  coverAllyId: string;
  maneuverError: string | null;
  guardAllyId: string;
  guardTechniqueId: string;
  guardDestination: string;
}

/** Everything a new round must wipe — reset WHOLESALE so a missed atom is impossible (#2423). */
function initialRoundState(): RoundScopedState {
  return {
    focusedContext: initialContext('focused'),
    passiveContexts: {
      'passive-physical': initialContext('passive-physical'),
      'passive-social': initialContext('passive-social'),
      'passive-mental': initialContext('passive-mental'),
    },
    selectedClashRef: null,
    strainByClash: {},
    submitted: false,
    submitError: null,
    pullDialogOpen: false,
    selectedPull: null,
    soulfrayAccepted: false,
    furyTierId: null,
    furyAnchorId: null,
    coverAllyId: '',
    maneuverError: null,
    guardAllyId: GUARD_ANYONE_VALUE,
    guardTechniqueId: GUARD_NO_TECHNIQUE_VALUE,
    guardDestination: GUARD_DESTINATION_AWAY,
  };
}

// ---------------------------------------------------------------------------
// Dispatch-job builders (extracted from handleSubmit to flatten its branching)
// ---------------------------------------------------------------------------

type DispatchFn = (params: DispatchActionRequest) => Promise<DispatchResult>;

type DispatchJob = () => Promise<DispatchResult>;

/**
 * Build the focused-slot dispatch job (if a technique is selected). Threads the
 * chosen single target onto the focused declaration (#1001a); the backend
 * resolves these PKs to instances scoped to the encounter.
 *
 * When a pull is selected, pull_resonance_id / pull_tier / pull_thread_ids are
 * merged into kwargs so the backend commits a CombatPull alongside the action.
 *
 * When a cast-time position is selected (#2206), `position_params` is merged
 * into kwargs: `{ destination_position_id }` for a "single"-shape technique,
 * or `{ position_a_id, position_b_id }` for a "pair"-shape technique.
 * Shape-aware per the selected action's `position_target_shape` — a
 * "none"/undefined-shape technique never gets `position_params`, even if
 * stale castPosition state is present (#2206 review finding).
 */
function buildFocusedJob(
  focusedContext: ActionContext,
  effortLevel: string,
  dispatchAction: DispatchFn,
  selectedPull: PullSelection | null,
  soulfrayAccepted: boolean,
  soulfrayWarning: SoulfrayWarningData | null,
  furyTierId: number | null,
  furyAnchorId: number | null,
  castPosition: CastPosition,
  positionTargetShape: PositionTargetShape
): DispatchJob | null {
  if (focusedContext.techniqueId === undefined) return null;

  const targetKwargs: Record<string, number> = {};
  if (focusedContext.targetId !== undefined) {
    if (focusedContext.targetKind === 'opponent') {
      targetKwargs.focused_opponent_target_id = focusedContext.targetId;
    } else if (focusedContext.targetKind === 'ally') {
      targetKwargs.focused_ally_target_id = focusedContext.targetId;
    }
  }

  const pullKwargs: Record<string, number | number[]> = {};
  if (selectedPull !== null) {
    pullKwargs.pull_resonance_id = selectedPull.resonance_id;
    pullKwargs.pull_tier = selectedPull.tier;
    pullKwargs.pull_thread_ids = selectedPull.thread_ids;
  }

  const furyKwargs: Record<string, number> = {};
  if (furyTierId !== null) furyKwargs.fury_commitment_id = furyTierId;
  if (furyAnchorId !== null) furyKwargs.fury_anchor_id = furyAnchorId;
  const soulfrayKwarg =
    soulfrayWarning !== null && soulfrayAccepted ? { confirm_soulfray_risk: true } : {};

  // Strictly per-shape — never derive position_params from mere presence of
  // castPosition fields, since stale state from a previously-selected
  // technique can otherwise leak into a differently-shaped technique's
  // dispatch (#2206 review finding).
  type PositionParams =
    | { destination_position_id: number }
    | { position_a_id: number; position_b_id: number };
  const positionKwargs: Record<string, PositionParams> = {};
  if (positionTargetShape === 'single' && castPosition?.destinationId !== undefined) {
    positionKwargs.position_params = { destination_position_id: castPosition.destinationId };
  } else if (
    positionTargetShape === 'pair' &&
    castPosition?.pairA !== undefined &&
    castPosition?.pairB !== undefined
  ) {
    positionKwargs.position_params = {
      position_a_id: castPosition.pairA,
      position_b_id: castPosition.pairB,
    };
  }

  return () =>
    dispatchAction({
      ref: {
        backend: 'COMBAT',
        technique_id: focusedContext.techniqueId ?? null,
        action_slot: 'focused',
      },
      kwargs: {
        effort_level: effortLevel,
        ...targetKwargs,
        ...pullKwargs,
        ...soulfrayKwarg,
        ...furyKwargs,
        ...positionKwargs,
      },
    });
}

/**
 * Build a dispatch job for each visible passive slot that has a technique.
 * Passives inherit the round effort declared on the focused slot.
 */
function buildPassiveJobs(
  visiblePassiveSlots: ActionSlot[],
  passiveContexts: Partial<Record<ActionSlot, ActionContext>>,
  effortLevel: string,
  dispatchAction: DispatchFn
): DispatchJob[] {
  const jobs: DispatchJob[] = [];
  for (const slot of visiblePassiveSlots) {
    const ctx = passiveContexts[slot];
    if (ctx != null && ctx.techniqueId !== undefined) {
      jobs.push(() =>
        dispatchAction({
          ref: {
            backend: 'COMBAT',
            technique_id: ctx.techniqueId ?? null,
            // `slot` is already the 'passive-<category>' string the backend's
            // CombatActionSlot expects — pass it straight through.
            action_slot: slot,
          },
          kwargs: { effort_level: effortLevel },
        })
      );
    }
  }
  return jobs;
}

/**
 * Build the clash-contribution dispatch job. technique_id goes in kwargs (NOT on
 * the ref) per plan Task 7.3: ActionRef.__post_init__ rejects both clash_id and
 * technique_id being set; see src/actions/types.py:137-155.
 *
 * When a pull is selected, pull_resonance_id / pull_tier / pull_thread_ids are
 * merged into kwargs so the backend commits a CombatPull alongside the clash.
 */
function buildClashJob(
  selectedClashRef: PlayerAction['ref'] | null,
  focusedContext: ActionContext,
  strainByClash: Record<number, number>,
  dispatchAction: DispatchFn,
  selectedPull: PullSelection | null
): DispatchJob | null {
  if (selectedClashRef === null || selectedClashRef.clash_id == null) return null;

  const clashId = selectedClashRef.clash_id;
  const strain = strainByClash[clashId] ?? 0;

  const pullKwargs: Record<string, number | number[]> = {};
  if (selectedPull !== null) {
    pullKwargs.pull_resonance_id = selectedPull.resonance_id;
    pullKwargs.pull_tier = selectedPull.tier;
    pullKwargs.pull_thread_ids = selectedPull.thread_ids;
  }

  return () =>
    dispatchAction({
      ref: {
        backend: 'COMBAT',
        clash_id: clashId,
        clash_action_slot: selectedClashRef.clash_action_slot ?? null,
      },
      kwargs: {
        // technique_id belongs here for clash contributions, not on the ref.
        technique_id: focusedContext.techniqueId,
        strain_commitment: strain,
        ...pullKwargs,
      },
    });
}

// ---------------------------------------------------------------------------
// ComboRow — renders one available combo as a button
// ---------------------------------------------------------------------------

interface ComboRowProps {
  combo: AvailableCombo;
  onUpgrade: (comboId: number) => void;
  isLoading: boolean;
}

function ComboRow({ combo, onUpgrade, isLoading }: ComboRowProps) {
  const isDisabled = !combo.known_by_participant || isLoading;
  const title = !combo.known_by_participant ? 'Combo not known' : undefined;

  return (
    <button
      type="button"
      disabled={isDisabled}
      title={title}
      onClick={() => onUpgrade(combo.combo_id)}
      data-testid={`combo-upgrade-btn-${combo.combo_id}`}
      className={cn(
        'w-full rounded border px-3 py-1.5 text-left text-xs font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        isDisabled
          ? 'border-border bg-muted text-muted-foreground'
          : 'border-primary/40 bg-primary/5 text-primary hover:bg-primary/10'
      )}
    >
      Upgrade to {combo.combo_name} ({combo.slot_count} slots)
    </button>
  );
}

// ---------------------------------------------------------------------------
// ClashContributionRow — renders one clash PlayerAction as a commit button
// ---------------------------------------------------------------------------

interface ClashContributionRowProps {
  action: PlayerAction;
  strainCommitment: number;
  onSelectClash: (ref: PlayerAction['ref']) => void;
  onStrainChange: (value: number) => void;
  isSelected: boolean;
  /** Strain slider max — reads ParticipantSerializer.available_strain. Fallback 10. */
  strainMax?: number;
}

function ClashContributionRow({
  action,
  strainCommitment,
  onSelectClash,
  onStrainChange,
  isSelected,
  strainMax = 10,
}: ClashContributionRowProps) {
  return (
    <div
      className="space-y-2 rounded border border-border bg-card/60 p-2"
      data-testid={`clash-contribution-row-${action.ref.clash_id ?? 'unknown'}`}
    >
      <button
        type="button"
        onClick={() => onSelectClash(action.ref)}
        className={cn(
          'w-full rounded px-2 py-1 text-left text-xs font-medium transition-colors',
          isSelected
            ? 'border border-primary bg-primary/10 text-primary'
            : 'border border-border bg-background text-foreground hover:border-primary/50'
        )}
        data-testid={`clash-commit-btn-${action.ref.clash_id ?? 'unknown'}`}
      >
        Commit to clash {action.display_name}
      </button>

      {/* Strain slider — only shown when this clash is selected */}
      {isSelected && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Strain commitment
            </span>
            <span className="font-mono text-xs text-foreground">{strainCommitment}</span>
          </div>
          <input
            type="range"
            min={0}
            max={strainMax}
            value={strainCommitment}
            onChange={(e) => onStrainChange(Number(e.target.value))}
            data-testid={`clash-strain-slider-${action.ref.clash_id ?? 'unknown'}`}
            className="w-full accent-primary"
            aria-label="Strain commitment"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Resolve the ally name attached to a declared maneuver (cover/interpose), or
 * null when a different maneuver is declared or no ally target is set (a null
 * focused_ally_target on interpose means "guard whoever is hit", #2207).
 */
function maneuverAllyName(
  expectedManeuver: string,
  declaredManeuver: string | null,
  ownRoundAction: RoundActionTyped | null,
  participants: Participant[]
): string | null {
  if (declaredManeuver !== expectedManeuver || ownRoundAction?.focused_ally_target == null) {
    return null;
  }
  const ally = participants.find((p) => p.id === ownRoundAction.focused_ally_target);
  return ally?.character_name ?? `participant #${ownRoundAction.focused_ally_target}`;
}

/**
 * The reason the submit button must refuse, or null when clear to dispatch —
 * mirrors the required-declaration gating (#1543/#2206).
 */
function submitBlockReason(args: {
  soulfrayWarning: SoulfrayWarningData | null;
  soulfrayAccepted: boolean;
  furyOverCap: boolean;
  positionRequirementMet: boolean;
}): string | null {
  if (args.soulfrayWarning !== null && !args.soulfrayAccepted) {
    return 'Accept the Soulfray risk to proceed.';
  }
  if (args.furyOverCap) {
    return 'Chosen fury tier exceeds your bond with the anchor.';
  }
  if (!args.positionRequirementMet) {
    return 'Select a position target for this technique.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// YourTurn
// ---------------------------------------------------------------------------

export function YourTurn({
  encounterId,
  characterId,
  characterSheetId,
  roundNumber,
  availableActions,
  readOnly = false,
  availableStrain,
  encounter = null,
  castPosition: castPositionProp,
  onCastPositionChange: onCastPositionChangeProp,
  onPositionShapeChange,
}: YourTurnProps) {
  const strainMax = availableStrain ?? 10;
  const queryClient = useQueryClient();
  // ---------------------------------------------------------------------------
  // Round-scoped state (#2423 finding 4) — consolidated into one object,
  // reset WHOLESALE on round advance (see initialRoundState above).
  // ---------------------------------------------------------------------------

  const [roundState, setRoundState] = useState<RoundScopedState>(initialRoundState);
  const {
    focusedContext,
    passiveContexts,
    selectedClashRef,
    strainByClash,
    submitted,
    submitError,
    pullDialogOpen,
    selectedPull,
    soulfrayAccepted,
    furyTierId,
    furyAnchorId,
    coverAllyId,
    maneuverError,
    guardAllyId,
    guardTechniqueId,
    guardDestination,
  } = roundState;

  // Per-field setter preserving React.SetStateAction semantics so every
  // existing call site (~60 across this file and child-component props)
  // compiles unchanged. Defined inside the component (not module level) so it
  // closes over `setRoundState`; `setRoundState` from `useState` is stable, so
  // the `useMemo([])` deps below are correct. `makeFieldSetter` itself is a
  // fresh function object every render, but that's harmless: it's a pure
  // factory (no closure over per-render values besides the stable
  // `setRoundState`) and nothing holds a reference to it by identity across
  // renders — only the memoized setters it returns are read, and those never
  // need to change.
  function makeFieldSetter<K extends keyof RoundScopedState>(key: K) {
    return (value: SetStateAction<RoundScopedState[K]>) => {
      setRoundState((prev) => ({
        ...prev,
        [key]:
          typeof value === 'function'
            ? (value as (p: RoundScopedState[K]) => RoundScopedState[K])(prev[key])
            : value,
      }));
    };
  }
  const setFocusedContext = useMemo(() => makeFieldSetter('focusedContext'), []);
  const setPassiveContexts = useMemo(() => makeFieldSetter('passiveContexts'), []);
  const setSelectedClashRef = useMemo(() => makeFieldSetter('selectedClashRef'), []);
  const setStrainByClash = useMemo(() => makeFieldSetter('strainByClash'), []);
  const setSubmitted = useMemo(() => makeFieldSetter('submitted'), []);
  const setSubmitError = useMemo(() => makeFieldSetter('submitError'), []);
  const setPullDialogOpen = useMemo(() => makeFieldSetter('pullDialogOpen'), []);
  const setSelectedPull = useMemo(() => makeFieldSetter('selectedPull'), []);
  const setSoulfrayAccepted = useMemo(() => makeFieldSetter('soulfrayAccepted'), []);
  const setFuryTierId = useMemo(() => makeFieldSetter('furyTierId'), []);
  const setFuryAnchorId = useMemo(() => makeFieldSetter('furyAnchorId'), []);
  const setCoverAllyId = useMemo(() => makeFieldSetter('coverAllyId'), []);
  const setManeuverError = useMemo(() => makeFieldSetter('maneuverError'), []);
  const setGuardAllyId = useMemo(() => makeFieldSetter('guardAllyId'), []);
  const setGuardTechniqueId = useMemo(() => makeFieldSetter('guardTechniqueId'), []);
  const setGuardDestination = useMemo(() => makeFieldSetter('guardDestination'), []);

  // Cast-time position selection for the focused technique (#2206). Controlled
  // by the caller when castPositionProp/onCastPositionChangeProp are supplied
  // (CombatRail lifts this above the rail tabs so the tactical-map tab
  // shares it); falls back to local state otherwise (e.g. standalone tests).
  // NOT part of RoundScopedState — stays lifted/controlled exactly as before.
  const [localCastPosition, setLocalCastPosition] = useState<CastPosition>({});
  const castPosition = castPositionProp ?? localCastPosition;
  const setCastPosition = onCastPositionChangeProp ?? setLocalCastPosition;

  // Did-mount guards for the two reset effects below (#2206 review finding).
  // Both effects' dependencies take on their "changed" value on first mount
  // too (standard React) — without a guard, mounting/remounting this panel
  // (e.g. switching from the Map tab back to Your Turn, which unmounts and
  // remounts inactive TabsContent) would fire `setCastPosition({})` and wipe
  // out a position the caller already lifted into `castPosition`/
  // `onCastPositionChange`. Skip the reset on the mount that establishes each
  // ref; only fire on genuine post-mount transitions.
  const roundResetMounted = useRef(false);
  const techniqueResetMounted = useRef(false);

  // Reset every round-scoped atom WHOLESALE when round advances (#2423) — a
  // single `setRoundState(initialRoundState())` makes "forgot to reset one
  // field" bugs structurally impossible.
  useEffect(() => {
    if (!roundResetMounted.current) {
      roundResetMounted.current = true;
      return;
    }
    setRoundState(initialRoundState());
    setCastPosition({});
  }, [roundNumber, setCastPosition]);

  // Reset cast-time position selection when the focused technique changes —
  // a position picked for a prior technique (e.g. a pair-shape technique's A/B)
  // must not silently leak into a differently-shaped technique's
  // `position_params` (#2206 review finding).
  useEffect(() => {
    if (!techniqueResetMounted.current) {
      techniqueResetMounted.current = true;
      return;
    }
    setCastPosition({});
  }, [focusedContext.techniqueId, setCastPosition]);

  // ---------------------------------------------------------------------------
  // Slot composition
  // ---------------------------------------------------------------------------

  const focusedCategory = resolveFocusedCategory(focusedContext, availableActions);
  const visiblePassiveSlots = passiveSlotsToRender(focusedCategory);

  // ---------------------------------------------------------------------------
  // Clash actions from availableActions (COMBAT backend + clash_id set)
  // ---------------------------------------------------------------------------

  const clashActions = availableActions.filter(
    (a) => a.ref.backend === 'combat' && a.ref.clash_id != null
  );

  // ---------------------------------------------------------------------------
  // Move-to-position actions from availableActions (registry backend, #532)
  // ---------------------------------------------------------------------------

  const moveActions = availableActions.filter(
    (a) => a.ref.backend === 'registry' && a.ref.registry_key === 'move_to_position'
  );

  // ---------------------------------------------------------------------------
  // Combos
  // ---------------------------------------------------------------------------

  const { data: availableCombos, isLoading: combosLoading } = useAvailableCombos(encounterId);
  const { mutate: upgradeCombo, isPending: upgradePending } = useUpgradeCombo(encounterId);

  // ---------------------------------------------------------------------------
  // Flee / Cover mutations
  // ---------------------------------------------------------------------------

  const { mutate: declareFlee, isPending: fleePending } = useFleeMutation(encounterId);
  const { mutate: declareCover, isPending: coverPending } = useCoverMutation(encounterId);
  const { mutate: declareGuard, isPending: guardPending } = useGuardMutation(
    encounterId,
    characterId
  );

  // ---------------------------------------------------------------------------
  // Flee / Cover — derived state
  // ---------------------------------------------------------------------------

  // Gates flee/cover on the declaring phase.
  const isDeclaringPhase = encounter?.status === 'declaring';

  // Derive the viewer's participant PK from the participants list — stable
  // regardless of whether current_round_actions is ordered or GM-visible (all
  // actions). characterSheetId matches character_sheet_id on the Participant row.
  const myParticipantId: number | null = (() => {
    const ps = encounter?.participants ?? [];
    const self = ps.find((p) => p.character_sheet_id === characterSheetId);
    return self?.id ?? null;
  })();

  // Own round action — find by participant PK, not positional [0].
  const ownRoundAction: RoundActionTyped | null = (() => {
    if (myParticipantId === null) return null;
    const actions = encounter?.current_round_actions ?? [];
    const match = actions.find(
      (a) =>
        typeof (a as RoundActionTyped).participant === 'number' &&
        (a as RoundActionTyped).participant === myParticipantId
    );
    return (match as RoundActionTyped) ?? null;
  })();

  // Server-derived ready lock (#2423): CombatRail's Radix tabs unmount inactive
  // TabsContent, so switching to the Map tab and back remounts this component
  // and resets local `submitted` to false — even though the server already has
  // this participant's round action marked ready. Deriving the lock from
  // ownRoundAction.is_ready (not just local state) survives that remount.
  const serverReady = ownRoundAction?.is_ready === true;

  const participants: Participant[] = encounter?.participants ?? [];

  // All active participants except self count as allies until covenant sides land (mirrors backend serializers.py note).
  const coverableAllies = participants.filter(
    (p) => p.status === 'active' && p.id !== myParticipantId
  );

  // Actor's position — the viewer's own participant's current_position.
  const actorPositionId: number | null = (() => {
    if (myParticipantId === null) return null;
    const self = (encounter?.participants ?? []).find((p) => p.id === myParticipantId);
    return self?.current_position?.id ?? null;
  })();

  // Focused-target options (#1001a): active opponents + allies. Opponents carry
  // their ObjectDB id for the applicable-pulls API; the dispatch uses the
  // CombatOpponent / CombatParticipant PK (`id`). Each option also carries
  // positionId for the reach pre-filter (#532).
  const focusedTargets: TargetOption[] = [
    ...(encounter?.opponents ?? [])
      .filter((o) => o.status === 'active')
      .map((o) => ({
        id: o.id,
        kind: 'opponent' as const,
        name: o.name,
        objectId: o.objectdb_id,
        positionId: o.current_position?.id ?? null,
      })),
    ...coverableAllies.map((p) => ({
      id: p.id,
      kind: 'ally' as const,
      name: p.character_name,
      positionId: p.current_position?.id ?? null,
    })),
  ];

  // The selected focused technique's action descriptor — one lookup shared by
  // the reach constraint (#532), the position-targeting shape (#2206), and the
  // soulfray/fury descriptor (#1543) below.
  const focusedCastDescriptor =
    focusedContext.techniqueId === undefined
      ? null
      : (availableActions.find((a) => a.ref.technique_id === focusedContext.techniqueId) ?? null);

  // Reach constraint for the currently selected focused technique (#532).
  const focusedTechniqueReach: string | null = focusedCastDescriptor?.reach ?? null;

  // Cast-time position-targeting shape for the currently selected focused
  // technique (#2206). Positions/edges data is the same source
  // CombatTacticalMap uses — EncounterDetail.position_nodes.
  const focusedTechniquePositionShape: PositionTargetShape =
    focusedCastDescriptor?.position_target_shape ?? 'none';
  const focusedPositions: PositionNode[] = encounter?.position_nodes ?? [];

  // Report the current shape to the caller (#2206) — lets the tactical-map tab
  // know whether map-click position-picking should be active, and keeps
  // knowing even after this panel unmounts on tab switch (CombatRail's
  // state persists across its children's mount/unmount).
  useEffect(() => {
    onPositionShapeChange?.(focusedTechniquePositionShape);
  }, [focusedTechniquePositionShape, onPositionShapeChange]);

  // Blocks Ready/submit while a required position slot is empty (#2206,
  // mirrors the fury/soulfray required-declaration gating below).
  const positionRequirementMet =
    focusedTechniquePositionShape === 'none' ||
    (focusedTechniquePositionShape === 'single' && castPosition.destinationId !== undefined) ||
    (focusedTechniquePositionShape === 'pair' &&
      castPosition.pairA !== undefined &&
      castPosition.pairB !== undefined);

  // Soulfray + fury descriptor for the currently selected focused cast (#1543).
  const soulfrayWarning = focusedCastDescriptor?.soulfray_warning ?? null;
  const furyTiers = focusedCastDescriptor?.available_fury_tiers ?? [];
  const furyAnchors = focusedCastDescriptor?.eligible_fury_anchors ?? [];
  const furyOverCap =
    furyTierId !== null &&
    furyAnchorId !== null &&
    (furyTiers.find((t) => t.id === furyTierId)?.depth ?? 0) >
      (furyAnchors.find((a) => a.id === furyAnchorId)?.provocation_cap ?? 0);

  // Current declared maneuver (from own round action).
  const declaredManeuver = ownRoundAction?.maneuver ?? null;

  // Resolve covered ally's name from participants list.
  const coveredAllyName = maneuverAllyName('cover', declaredManeuver, ownRoundAction, participants);

  // Resolve guarded ally's name from participants list (#2207). null
  // focused_ally_target on an interpose maneuver means "guard whoever is hit."
  const guardedAllyName = maneuverAllyName(
    'interpose',
    declaredManeuver,
    ownRoundAction,
    participants
  );

  // Techniques offered as a Guard's optional protective technique (#2207): any
  // available combat-cast technique whose protective_flavor classifier resolved.
  // 'redirect' techniques resolve since #2210 — the destination picker below
  // surfaces when one is selected.
  const protectiveTechniques = availableActions.filter(
    (a) => a.ref.backend === 'combat' && a.protective_flavor != null && a.ref.technique_id != null
  );

  // The currently-selected guard technique's protective flavor (#2210) — gates
  // the redirect destination picker.
  const selectedGuardTechnique = protectiveTechniques.find(
    (a) => String(a.ref.technique_id) === guardTechniqueId
  );
  const isRedirectGuardTechnique = selectedGuardTechnique?.protective_flavor === 'redirect';

  // ---------------------------------------------------------------------------
  // Dispatch
  // ---------------------------------------------------------------------------

  const { mutateAsync: dispatchAction, isPending: dispatchPending } =
    useDispatchPlayerAction(characterId);

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (submitted || serverReady || dispatchPending) return;

    setSubmitError(null);

    const blockReason = submitBlockReason({
      soulfrayWarning,
      soulfrayAccepted,
      furyOverCap,
      positionRequirementMet,
    });
    if (blockReason !== null) {
      setSubmitError(blockReason);
      return;
    }

    // The round effort comes from the focused slot and applies to every
    // declaration (focused + passives). The COMBAT dispatch requires
    // effort_level in kwargs on every ref or it rejects (UNKNOWN_ACTION_REF).
    const effortLevel = EFFORT_TO_BACKEND[focusedContext.effort];

    // Submission order per plan: focused first, then passives, then clashes
    // (focused first guarantees the server sees focused before passives).
    // selectedPull (if any) rides on focused and clash kwargs — the backend
    // commits a CombatPull when those kwargs are present.
    const focusedJob = buildFocusedJob(
      focusedContext,
      effortLevel,
      dispatchAction,
      selectedPull,
      soulfrayAccepted,
      soulfrayWarning,
      furyTierId,
      furyAnchorId,
      castPosition,
      focusedTechniquePositionShape
    );
    const passiveJobs = buildPassiveJobs(
      visiblePassiveSlots,
      passiveContexts,
      effortLevel,
      dispatchAction
    );
    const clashJob = buildClashJob(
      selectedClashRef,
      focusedContext,
      strainByClash,
      dispatchAction,
      selectedPull
    );

    const dispatchJobs: DispatchJob[] = [
      ...(focusedJob ? [focusedJob] : []),
      ...passiveJobs,
      ...(clashJob ? [clashJob] : []),
    ];

    try {
      for (const job of dispatchJobs) {
        const result = await job();
        // The dispatch endpoint always resolves 200 for a business-rule
        // rejection (only a structural ref error is a 400) — success:false
        // must be checked explicitly, or an honest-failure job would silently
        // flip the local `submitted`/ready state (#2423).
        if (isDispatchFailure(result)) {
          setSubmitError(result.message ?? 'Submit failed. Try again.');
          return;
        }
      }
      // PaceMode.READY early-resolution (#3067): `declare_action` resets
      // `is_ready=False` on every declaration, so submitting alone never
      // marks the participant ready — a READY-pace encounter would then
      // wait out the TIMED-style sweep instead of resolving the instant
      // everyone's in. Dispatch the same `combat_ready` registry action the
      // REST `ready` endpoint / telnet `combat ready` use, mirroring
      // `ReadyAction.execute` -> `maybe_resolve_on_ready`. Only meaningful in
      // READY pace — TIMED resolves on its timer and MANUAL waits for the GM,
      // so this is skipped for both to leave their behavior unchanged.
      if (encounter?.pace_mode === 'ready') {
        const readyResult = await dispatchAction({
          ref: { backend: 'registry', registry_key: 'combat_ready' },
          kwargs: {},
        });
        if (isDispatchFailure(readyResult)) {
          setSubmitError(readyResult.message ?? 'Submit failed. Try again.');
          return;
        }
      }
      setSubmitted(true);
      // Refresh the encounter (carries the server's is_ready) and the "Last
      // Outcome" panel now that every declared job succeeded (#2423).
      queryClient
        .invalidateQueries({ queryKey: combatKeys.encounter(encounterId) })
        .catch(() => {});
      invalidateConsequenceOutcomes(queryClient);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submit failed. Try again.';
      setSubmitError(message);
    }
  }

  // ---------------------------------------------------------------------------
  // Flee / Cover handlers
  // ---------------------------------------------------------------------------

  function handleFlee() {
    setManeuverError(null);
    declareFlee(undefined, {
      onError: (err) => {
        setManeuverError(err instanceof Error ? err.message : 'Failed to declare flee');
      },
    });
  }

  function handleCover() {
    const allyId = parseInt(coverAllyId, 10);
    if (!allyId) {
      setManeuverError('Select an ally to cover');
      return;
    }
    setManeuverError(null);
    declareCover(allyId, {
      onError: (err) => {
        setManeuverError(err instanceof Error ? err.message : 'Failed to declare cover');
      },
    });
  }

  function handleGuard() {
    setManeuverError(null);
    const allyParticipantId =
      guardAllyId === GUARD_ANYONE_VALUE ? null : parseInt(guardAllyId, 10) || null;
    const techniqueId =
      guardTechniqueId === GUARD_NO_TECHNIQUE_VALUE ? null : parseInt(guardTechniqueId, 10) || null;
    // Redirect destination (#2210) — "away" (the sentinel default) sends
    // neither kwarg, matching the backend's own null-means-away default.
    let redirectOpponentTargetId: number | null = null;
    let redirectObjectTargetId: number | null = null;
    if (guardDestination.startsWith(GUARD_DESTINATION_OPPONENT_PREFIX)) {
      redirectOpponentTargetId =
        parseInt(guardDestination.slice(GUARD_DESTINATION_OPPONENT_PREFIX.length), 10) || null;
    } else if (guardDestination.startsWith(GUARD_DESTINATION_OBJECT_PREFIX)) {
      redirectObjectTargetId =
        parseInt(guardDestination.slice(GUARD_DESTINATION_OBJECT_PREFIX.length), 10) || null;
    }
    declareGuard(
      { allyParticipantId, techniqueId, redirectOpponentTargetId, redirectObjectTargetId },
      {
        onSuccess: (result) => {
          // The generic dispatch endpoint always resolves 200 — a business-rule
          // rejection (e.g. "not in active round") surfaces as success:false,
          // not a thrown error, so it must be checked explicitly here.
          if (result?.success === false) {
            setManeuverError(result.message ?? 'Failed to declare guard');
          }
        },
        onError: (err) => {
          setManeuverError(err instanceof Error ? err.message : 'Failed to declare guard');
        },
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const isLocked = readOnly || submitted || serverReady;

  return (
    <div className="space-y-4" data-testid="your-turn-section">
      {/* Submitted / ready badge */}
      {(submitted || serverReady) && (
        <div
          className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-center text-sm font-medium text-emerald-300"
          data-testid="ready-badge"
        >
          Ready: waiting for round to advance
        </div>
      )}

      {/* Focused slot */}
      <div>
        <p
          className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          title="Your primary declared action this round, the technique or maneuver you're committing to."
        >
          Focused Action
        </p>
        <ActionDeclarationCard
          characterId={characterId}
          characterSheetId={characterSheetId}
          actionContext={focusedContext}
          onContextChange={(next) => {
            setSubmitError(null);
            setFocusedContext(next);
          }}
          readOnly={isLocked}
          targets={focusedTargets}
          reach={focusedTechniqueReach}
          actorPositionId={actorPositionId}
          positionAdjacency={encounter?.position_adjacency ?? []}
          positionTargetShape={focusedTechniquePositionShape}
          positions={focusedPositions}
          castPosition={castPosition}
          onCastPositionChange={setCastPosition}
        />
        {soulfrayWarning !== null && (
          <SoulfrayAcceptGate
            warning={soulfrayWarning}
            techniqueName={focusedCastDescriptor?.display_name ?? 'Cast'}
            animaCost={0}
            accepted={soulfrayAccepted}
            onAcceptChange={setSoulfrayAccepted}
            disabled={isLocked}
          />
        )}
        {furyTiers.length > 0 && (
          <FuryDeclaration
            tiers={furyTiers}
            anchors={furyAnchors}
            tierId={furyTierId}
            anchorId={furyAnchorId}
            onTierChange={setFuryTierId}
            onAnchorChange={setFuryAnchorId}
            disabled={isLocked}
          />
        )}
      </div>

      {/* Clash contribution subsection — shown when clash actions are available */}
      {clashActions.length > 0 && (
        <div className="space-y-2" data-testid="clash-contributions-section">
          <p
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            title="Add strain to an ongoing team Clash instead of acting alone this round."
          >
            Clash Contributions
          </p>
          {clashActions.map((action) => {
            const clashId = action.ref.clash_id;
            if (clashId == null) return null;
            return (
              <ClashContributionRow
                key={clashId}
                action={action}
                strainCommitment={strainByClash[clashId] ?? 0}
                onSelectClash={(ref) => {
                  setSelectedClashRef(selectedClashRef?.clash_id === ref.clash_id ? null : ref);
                  // Update focused context with the strain commitment.
                  setFocusedContext((prev) => ({
                    ...prev,
                    strainCommitment: strainByClash[clashId] ?? 0,
                  }));
                }}
                onStrainChange={(value) => {
                  setStrainByClash((prev) => ({ ...prev, [clashId]: value }));
                  // Mirror to focusedContext.strainCommitment so the card sees it.
                  setFocusedContext((prev) => ({ ...prev, strainCommitment: value }));
                }}
                isSelected={selectedClashRef?.clash_id === clashId}
                strainMax={strainMax}
              />
            );
          })}
        </div>
      )}

      {/* Move-to-position actions (#532) — shown when adjacent open positions exist */}
      <MovementActions
        actions={moveActions}
        isLocked={isLocked}
        dispatchAction={dispatchAction}
        onDispatched={() => {
          queryClient
            .invalidateQueries({ queryKey: combatKeys.encounter(encounterId) })
            .catch(() => {});
        }}
      />

      {/* Passive slots — only non-focused-category slots */}
      {visiblePassiveSlots.length > 0 && (
        <div className="space-y-3">
          <p
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            title="Secondary declarations in categories your Focused Action doesn't use; they resolve alongside it."
          >
            Passive Actions
          </p>
          {visiblePassiveSlots.map((slot) => (
            <ActionDeclarationCard
              key={slot}
              characterId={characterId}
              characterSheetId={characterSheetId}
              actionContext={passiveContexts[slot] ?? initialContext(slot)}
              onContextChange={(next) => {
                setSubmitError(null);
                setPassiveContexts((prev) => ({ ...prev, [slot]: next }));
              }}
              readOnly={isLocked}
            />
          ))}
        </div>
      )}

      {/* Combo upgrade row — shown when combos are available */}
      {availableCombos !== undefined && availableCombos.length > 0 && (
        <div className="space-y-2" data-testid="combo-upgrade-section">
          <p
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            title="Upgrade your Focused Action into a known multi-slot combo, if you qualify this round."
          >
            Combo Upgrades
          </p>
          {availableCombos.map((combo) => (
            <ComboRow
              key={combo.combo_id}
              combo={combo}
              onUpgrade={(id) => upgradeCombo(id)}
              isLoading={combosLoading || upgradePending}
            />
          ))}
        </div>
      )}

      {/* Thread Pull row — inline pull selection for combat cast/clash dispatch */}
      <div
        className="space-y-1 rounded border border-primary/20 bg-primary/5 px-3 py-2"
        data-testid="thread-pull-row"
      >
        <div className="flex items-center justify-between">
          <span
            className="text-xs font-semibold text-primary/80"
            title="Draw on a bonded Thread to empower this round's action."
          >
            ✦ Thread Pull
          </span>
          <div className="flex gap-2">
            {selectedPull !== null && (
              <button
                type="button"
                onClick={() => setSelectedPull(null)}
                disabled={isLocked}
                data-testid="clear-pull-btn"
                className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive transition-colors hover:bg-destructive/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => setPullDialogOpen(true)}
              disabled={isLocked}
              data-testid="open-pull-dialog-btn"
              className="rounded border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {selectedPull === null ? 'Pull Threads' : 'Change Pull'}
            </button>
          </div>
        </div>
        {selectedPull !== null && (
          <p className="text-[10px] text-primary/70" data-testid="selected-pull-summary">
            Tier {selectedPull.tier} pull: {selectedPull.thread_ids.length} thread
            {selectedPull.thread_ids.length === 1 ? '' : 's'} selected
          </p>
        )}
      </div>

      <ThreadPullDialog
        characterSheetId={characterSheetId}
        open={pullDialogOpen}
        onClose={() => setPullDialogOpen(false)}
        onSelect={(selection) => {
          setSelectedPull(selection);
          setPullDialogOpen(false);
        }}
      />

      {/* Flee / Cover declaration cluster — always rendered when encounter is non-null; controls disabled outside the declaring phase */}
      {encounter != null && (
        <div className="space-y-2" data-testid="maneuver-declaration-section">
          <p
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            title="Flee the encounter, Cover an ally, or Guard an ally with your body or a protective technique, instead of declaring an offensive or defensive action."
          >
            Maneuvers
          </p>

          {/* Declared-state display */}
          {declaredManeuver === 'flee' && (
            <div
              className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
              data-testid="flee-declared-badge"
            >
              Fleeing: resolves at end of round
            </div>
          )}
          {declaredManeuver === 'cover' && (
            <div
              className="rounded border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-300"
              data-testid="cover-declared-badge"
            >
              Covering {coveredAllyName ?? 'ally'}
            </div>
          )}
          {declaredManeuver === 'interpose' && (
            <div
              className="rounded border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-xs text-violet-300"
              data-testid="guard-declared-badge"
            >
              Guarding {guardedAllyName ?? 'any ally hit this round'}
            </div>
          )}

          {/* Flee button — only when not already declared a flee maneuver */}
          {declaredManeuver !== 'flee' && (
            <button
              type="button"
              disabled={isLocked || !isDeclaringPhase || fleePending}
              onClick={handleFlee}
              data-testid="flee-btn"
              className={cn(
                'w-full rounded-md border px-4 py-2 text-sm font-semibold transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isLocked || !isDeclaringPhase
                  ? 'border-border bg-muted text-muted-foreground'
                  : 'border-destructive bg-destructive/10 text-destructive hover:bg-destructive/20'
              )}
            >
              {fleePending ? 'Declaring flee…' : 'Flee'}
            </button>
          )}

          {/* Cover control — ally picker + confirm button */}
          {declaredManeuver !== 'cover' && (
            <div className="space-y-1.5" data-testid="cover-control">
              <Select
                value={coverAllyId}
                onValueChange={setCoverAllyId}
                disabled={isLocked || !isDeclaringPhase || coverPending}
              >
                <SelectTrigger data-testid="cover-ally-select" className="h-8 text-xs">
                  <SelectValue placeholder="Cover an ally…" />
                </SelectTrigger>
                <SelectContent>
                  {coverableAllies.map((ally) => (
                    <SelectItem key={ally.id} value={String(ally.id)}>
                      {ally.character_name}
                    </SelectItem>
                  ))}
                  {coverableAllies.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      No allies available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <button
                type="button"
                disabled={isLocked || !isDeclaringPhase || coverPending || coverAllyId === ''}
                onClick={handleCover}
                data-testid="cover-confirm-btn"
                className={cn(
                  'w-full rounded-md border px-4 py-1.5 text-xs font-semibold transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  isLocked || !isDeclaringPhase || coverAllyId === ''
                    ? 'border-border bg-muted text-muted-foreground'
                    : 'border-sky-500/60 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20'
                )}
              >
                {coverPending ? 'Declaring cover…' : 'Confirm Cover'}
              </button>
            </div>
          )}

          {/* Guard control — ward picker + optional protective-technique select + confirm (#2207) */}
          {declaredManeuver !== 'interpose' && (
            <div className="space-y-1.5" data-testid="guard-control">
              <Select
                value={guardAllyId}
                onValueChange={setGuardAllyId}
                disabled={isLocked || !isDeclaringPhase || guardPending}
              >
                <SelectTrigger data-testid="guard-ally-select" className="h-8 text-xs">
                  <SelectValue placeholder="Guard anyone…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={GUARD_ANYONE_VALUE}>Anyone (guard whoever is hit)</SelectItem>
                  {coverableAllies.map((ally) => (
                    <SelectItem key={ally.id} value={String(ally.id)}>
                      {ally.character_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {protectiveTechniques.length > 0 && (
                <Select
                  value={guardTechniqueId}
                  onValueChange={setGuardTechniqueId}
                  disabled={isLocked || !isDeclaringPhase || guardPending}
                >
                  <SelectTrigger data-testid="guard-technique-select" className="h-8 text-xs">
                    <SelectValue placeholder="No protective technique" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={GUARD_NO_TECHNIQUE_VALUE}>
                      No protective technique (mundane)
                    </SelectItem>
                    {protectiveTechniques.map((action) => (
                      <SelectItem
                        key={action.ref.technique_id ?? action.display_name}
                        value={String(action.ref.technique_id)}
                      >
                        {action.display_name}
                        {action.protective_flavor != null
                          ? ` (${PROTECTIVE_FLAVOR_LABELS[action.protective_flavor] ?? action.protective_flavor})`
                          : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {isRedirectGuardTechnique && (
                <Select
                  value={guardDestination}
                  onValueChange={setGuardDestination}
                  disabled={isLocked || !isDeclaringPhase || guardPending}
                >
                  <SelectTrigger
                    data-testid="guard-redirect-destination-select"
                    className="h-8 text-xs"
                  >
                    <SelectValue placeholder="Redirect destination" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={GUARD_DESTINATION_AWAY}>Away (default)</SelectItem>
                    {(encounter?.opponents ?? []).map((opponent) => (
                      <SelectItem
                        key={`opp-${opponent.id}`}
                        value={`${GUARD_DESTINATION_OPPONENT_PREFIX}${opponent.id}`}
                      >
                        {opponent.name}
                      </SelectItem>
                    ))}
                    {(encounter?.volatile_objects ?? []).map((volatileObject) => (
                      <SelectItem
                        key={`obj-${volatileObject.id}`}
                        value={`${GUARD_DESTINATION_OBJECT_PREFIX}${volatileObject.id}`}
                      >
                        {volatileObject.name}
                        {volatileObject.position_name != null
                          ? ` (${volatileObject.position_name})`
                          : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <button
                type="button"
                disabled={isLocked || !isDeclaringPhase || guardPending}
                onClick={handleGuard}
                data-testid="guard-confirm-btn"
                className={cn(
                  'w-full rounded-md border px-4 py-1.5 text-xs font-semibold transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  isLocked || !isDeclaringPhase
                    ? 'border-border bg-muted text-muted-foreground'
                    : 'border-violet-500/60 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20'
                )}
              >
                {guardPending ? 'Declaring guard…' : 'Guard'}
              </button>
            </div>
          )}

          {/* Maneuver error display */}
          {maneuverError !== null && (
            <p role="alert" className="text-sm text-destructive" data-testid="maneuver-error">
              {maneuverError}
            </p>
          )}
        </div>
      )}

      {/* Submit declarations button */}
      <button
        type="button"
        disabled={
          isLocked ||
          dispatchPending ||
          (soulfrayWarning !== null && !soulfrayAccepted) ||
          furyOverCap ||
          !positionRequirementMet
        }
        onClick={() => {
          handleSubmit().catch(() => {});
        }}
        data-testid="submit-declarations-btn"
        className={cn(
          'w-full rounded-md border px-4 py-2 text-sm font-semibold transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isLocked
            ? 'border-border bg-muted text-muted-foreground'
            : 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
        )}
      >
        {dispatchPending
          ? 'Submitting…'
          : encounter?.pace_mode === 'ready'
            ? 'Submit declarations · mark ready'
            : 'Submit declarations'}
      </button>

      {/* Inline submit error — shown when a dispatch rejects */}
      {submitError !== null && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {submitError}
        </p>
      )}
    </div>
  );
}
